var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from "react";
import { classNames } from "@bookingcom/bui-core/utilities/classNames";
import { isScreenSmall, normalizeKey, isRTL, nextFrame, } from "@bookingcom/bui-core/utilities/helpers";
import { getTabsTransforms } from "@bookingcom/bui-core/utilities/rendering";
import Keys from "@bookingcom/bui-core/constants/keys";
import ArrowMenuIcon from "@bookingcom/bui-assets-react/streamline/ArrowMenuIcon";
import Icon from "../Icon/index.js";
import DropdownMenu from "../DropdownMenu/index.js";
import useIsomorphicLayoutEffect from "../../hooks/useIsomorphicLayoutEffect.js";
import TabTriggerPrivate from "./TabTriggerPrivate.js";
import TabTrigger from "./TabTrigger.js";
import { useTabContext } from "./Tab.context.js";
import styles from "@bookingcom/bui-core/css/Tab.module.css";
var TabTriggerList = function (props) {
    var children = props.children;
    var _a = useTabContext(), variant = _a.variant, activeTabId = _a.activeTabId, fillEqually = _a.fillEqually, vertical = _a.vertical, moreLabel = _a.moreLabel, rootRef = _a.rootRef, navRef = _a.navRef, moreRef = _a.moreRef, changeTab = _a.changeTab, buttonRefs = _a.buttonRefs;
    var _b = React.useState(false), animated = _b[0], setAnimated = _b[1];
    var triggerListClassNames = classNames(styles["trigger-list"], variant && styles["trigger-list--variant-".concat(variant)], animated && styles["trigger-list--animated"]);
    var triggers = [];
    var activeIndex = 0;
    var isLinkBehavior = false;
    var triggerTextCombined = "";
    React.Children.forEach(children, function (child) {
        if (!child || child.type !== TabTrigger)
            return;
        triggers.push(child);
        triggerTextCombined += child.props.text;
        if (child.props.href)
            isLinkBehavior = true;
        if (activeTabId && child.props.id === activeTabId)
            activeIndex = triggers.length - 1;
    });
    var initialRender = React.useRef(true);
    var prevActiveIndexRef = React.useRef(activeIndex);
    var _c = React.useState({}), navStyle = _c[0], setNavStyle = _c[1];
    var _d = React.useState(false), previewMode = _d[0], setPreviewMode = _d[1];
    var _e = React.useState(true), isMobile = _e[0], setMobile = _e[1];
    var _f = React.useState(triggers.length), visibleButtons = _f[0], setVisibleButtons = _f[1];
    var triggersCount = triggers.length;
    var firstTriggerId = triggers[0].props.id;
    var getItemStyle = function () {
        if (!fillEqually || previewMode)
            return undefined;
        return { width: "".concat(100 / triggers.length, "%") };
    };
    var isMoreShown = previewMode || visibleButtons < triggers.length;
    var moreClassName = classNames(styles.item, styles["item--more"], isMoreShown && !isMobile && styles["item--more-active"]);
    var dropdownItems = triggers.slice(visibleButtons).map(function (trigger) {
        var _a = trigger.props, text = _a.text, icon = _a.icon, bubble = _a.bubble, title = _a.title, href = _a.href, native = _a.native, linkAttributes = _a.linkAttributes;
        return {
            text: text,
            icon: icon,
            bubble: bubble,
            href: href,
            native: native,
            endSlot: title,
            attributes: linkAttributes,
        };
    });
    var handleKeys = function (event) {
        var lastIndex = triggers.length - 1;
        var nextIndex = activeIndex === lastIndex ? 0 : activeIndex + 1;
        var prevIndex = activeIndex === 0 ? lastIndex : activeIndex - 1;
        var hasNativeTriggers = triggers.filter(function (trigger) { return trigger.props.native; }).length > 0;
        if (hasNativeTriggers)
            return;
        switch (normalizeKey(event.key)) {
            case Keys.RIGHT:
                changeTab(triggers[nextIndex].props.id, nextIndex, { focus: true });
                break;
            case Keys.LEFT:
                changeTab(triggers[prevIndex].props.id, prevIndex, { focus: true });
                break;
            case Keys.HOME:
                changeTab(triggers[0].props.id, 0, { focus: true });
                break;
            case Keys.END:
                changeTab(triggers[lastIndex].props.id, lastIndex, { focus: true });
                break;
            default:
                break;
        }
    };
    var handleDropdownItemClick = function (_, index) {
        var tabIndex = index + visibleButtons;
        var tab = triggers[tabIndex];
        changeTab(tab.props.id, tabIndex);
    };
    // Scrolls to active tab on first render
    // and does smooth scroll on user navigating between tabs
    useIsomorphicLayoutEffect(function () {
        if (!navRef.current || !buttonRefs.current)
            return;
        var activeButton = buttonRefs.current[activeIndex];
        var shouldHaveAtLeast = (navRef.current.clientWidth - activeButton.clientWidth) / 2;
        // When tab is larger than container width —
        // align it to start or end depending on content direction
        var alignmentCompensation = isRTL()
            ? activeButton.offsetLeft +
                (activeButton.clientWidth - navRef.current.clientWidth)
            : activeButton.offsetLeft;
        var scrollLeft = activeButton.clientWidth > navRef.current.clientWidth
            ? alignmentCompensation
            : activeButton.offsetLeft - shouldHaveAtLeast;
        navRef.current.scrollTo({
            left: scrollLeft,
            behavior: initialRender.current ? "auto" : "smooth",
        });
        initialRender.current = false;
    }, [activeIndex, buttonRefs, navRef]);
    useIsomorphicLayoutEffect(function () {
        if (!activeTabId) {
            changeTab(firstTriggerId, 0);
        }
    }, [firstTriggerId, activeTabId, changeTab]);
    useIsomorphicLayoutEffect(function () {
        var handleResize = function () {
            if (fillEqually)
                return;
            var isMobile = isScreenSmall();
            setMobile(isMobile);
            if (!isMobile)
                setPreviewMode(true);
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return function () { return window.removeEventListener("resize", handleResize); };
    }, [fillEqually, triggersCount]);
    React.useEffect(function () {
        if (triggersCount < activeIndex) {
            changeTab(firstTriggerId, 0, { focus: true });
        }
    }, [
        triggerTextCombined,
        activeIndex,
        changeTab,
        triggersCount,
        firstTriggerId,
    ]);
    useIsomorphicLayoutEffect(function () {
        var _a;
        if (variant !== "underlined" ||
            (activeIndex >= visibleButtons && !isMobile)) {
            return;
        }
        var activeButton = (_a = buttonRefs.current) === null || _a === void 0 ? void 0 : _a[activeIndex];
        var style = getTabsTransforms(activeButton);
        var prevActiveIndex = prevActiveIndexRef.current;
        prevActiveIndexRef.current = activeIndex;
        if (prevActiveIndex !== activeIndex)
            setAnimated(true);
        nextFrame(function () {
            setNavStyle(style);
        });
    }, [activeIndex, isMobile, navRef, variant, visibleButtons, buttonRefs]);
    // Preview buttons to count new sizes
    useIsomorphicLayoutEffect(function () {
        var _a;
        if (!previewMode)
            return;
        if (!rootRef.current)
            return;
        var isOverflow = false;
        var buttonsWidth = 0;
        var nextVisibleButtons = 0;
        var moreButtonWidth = (moreRef.current && moreRef.current.clientWidth) || 0;
        var rootEl = rootRef.current;
        var rootWidth = rootEl.clientWidth;
        (_a = buttonRefs.current) === null || _a === void 0 ? void 0 : _a.forEach(function (el, index) {
            var _a;
            // Whenever possible, use parent clientWidth (<li>) since button has borders
            // which are not taken into account when measuring with clientWidth
            buttonsWidth += (_a = el === null || el === void 0 ? void 0 : el.parentElement) === null || _a === void 0 ? void 0 : _a.clientWidth;
            var exceedsWidth = buttonsWidth > rootWidth;
            // More button may not fit in the free space, so we'll have to hide previous item as well
            if (exceedsWidth &&
                !isOverflow &&
                buttonsWidth + (moreButtonWidth || 0) > rootWidth) {
                var lastEl = buttonRefs.current
                    ? buttonRefs.current[index - 1]
                    : undefined;
                nextVisibleButtons -= 1;
                if (lastEl && lastEl.clientWidth < moreButtonWidth)
                    nextVisibleButtons -= 1;
            }
            if (exceedsWidth)
                isOverflow = true;
            if (!isOverflow)
                nextVisibleButtons += 1;
        });
        if (visibleButtons !== nextVisibleButtons) {
            setVisibleButtons(nextVisibleButtons);
        }
        setPreviewMode(false);
    }, [previewMode]);
    var handleTransitionEnd = function (event) {
        if (variant === "underlined" &&
            event.target === navRef.current &&
            event.pseudoElement === "::after") {
            setAnimated(false);
        }
    };
    return (React.createElement("div", { className: triggerListClassNames, style: {
            "--bui-tab-underline-scale-x": navStyle.width,
            "--bui-tab-underline-transform-x": navStyle.left,
        } },
        React.createElement("ul", { className: styles.nav, role: isLinkBehavior ? undefined : "tablist", ref: navRef, onTransitionEnd: handleTransitionEnd },
            triggers.map(function (trigger, index) {
                var isOverflow = index + 1 > visibleButtons && !previewMode;
                return (React.createElement(TabTriggerPrivate, __assign({}, trigger.props, { key: trigger.props.id, isOverflow: isOverflow, triggerIndex: index, handleKeys: handleKeys, isMobile: isMobile, getItemStyle: getItemStyle })));
            }),
            dropdownItems && (React.createElement("li", { className: moreClassName, role: "presentation", ref: moreRef, style: getItemStyle() },
                React.createElement(DropdownMenu, { items: dropdownItems, onItemChoose: handleDropdownItemClick, position: "bottom-end" }, function (attributes) { return (React.createElement("button", __assign({}, attributes, { className: styles.link, type: "button", role: "tab" }),
                    moreLabel,
                    !vertical && (React.createElement(Icon, { svg: ArrowMenuIcon, size: "small", className: styles.iconMore })))); }))))));
};
export default TabTriggerList;
